import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  StopOutlined,
  BarChartOutlined,
  DownloadOutlined
} from '@ant-design/icons';

import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { useResponsive } from '../../hooks/useResponsive';
import { IAssessmentsState } from '../../store/reducers/assessments/types';
import { IClassesState } from '../../store/reducers/classes/types';
import { IAnswerSheetsState } from '../../store/reducers/answer_sheets/types';
import { IGradingScalesState } from '../../store/reducers/grading_scales/types';

import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { PageContainer } from '../../components/layouts/PageLayouts/PageContainer/PageContainer';
import { ContentCard } from '../../components/layouts/PageLayouts/ContentCard/ContentCard';
import { BusinessButton } from '../../components/common/buttons/BusinessButton/BusinessButton';
import { notificationController } from '../../controllers/notificationController';
import { CreateAssessmentModal, AssessmentFormData } from '../../components/common/modals/CreateAssessmentModal';
import { EditAssessmentModal, AssessmentFormData as EditFormData } from '../../components/common/modals/EditAssessmentModal/EditAssessmentModal';
import { DeleteConfirmModal } from '../../components/common/modals/DeleteConfirmModal';

// Styled Components
const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .title {
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &::before {
      content: '📋';
      font-size: 28px;
    }
  }
  
  .subtitle {
    font-size: 14px;
    color: rgba(44, 62, 80, 0.7);
    margin: 4px 0 0 36px;
  }
  
  @media (max-width: 768px) {
    .title {
      font-size: 20px;
      
      &::before {
        font-size: 24px;
      }
    }
    
    .subtitle {
      margin-left: 32px;
      font-size: 13px;
    }
  }
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }
`;

const SearchInput = styled.div`
  position: relative;
  min-width: 280px;
  
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
  
  input {
    width: 100%;
    padding: 10px 12px 10px 40px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    font-size: 14px;
    background: white;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #1677ff;
      box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
    }
    
    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
    }
  }
  
  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

const CreateButton = styled(BusinessButton)`
  min-width: 160px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
  }
`;

// Assessment Cards Styles
const AssessmentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 16px;
  }
`;

const AssessmentCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 18px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: rgba(22, 119, 255, 0.3);
    
    .actions {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #4096ff 0%, #1677ff 50%, #0958d9 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @media (max-width: 1024px) {
    padding: 20px;
    
    &:hover {
      transform: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
    
    &::before {
      opacity: 1;
    }
    
    .actions {
      opacity: 1;
      transform: translateY(0);
    }
    
    &:active {
      transform: scale(0.98);
    }
  }
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  
  .icon {
    font-size: 18px;
    flex-shrink: 0;
  }
  
  .name {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
    line-height: 1.3;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .date {
    font-size: 11px;
    color: #6B7280;
    font-weight: 500;
    background: rgba(107, 114, 128, 0.1);
    padding: 3px 8px;
    border-radius: 5px;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

const CardInfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
`;

const CardInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  
  .row-icon {
    font-size: 12px;
    width: 18px;
    text-align: center;
    flex-shrink: 0;
  }
  
  .row-label {
    color: #6B7280;
    min-width: 55px;
    flex-shrink: 0;
  }
  
  .row-value {
    color: #1a1a1a;
    font-weight: 500;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StatusText = styled.span<{ status: 'draft' | 'scheduled' | 'active' | 'completed' }>`
  font-weight: 600;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  
  ${props => {
    switch (props.status) {
      case 'draft':
        return `color: #6B7280;`;
      case 'scheduled':
        return `color: #2563EB;`;
      case 'active':
        return `color: #16A34A;`;
      case 'completed':
        return `color: #7C3AED;`;
      default:
        return `color: #6B7280;`;
    }
  }}
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    ${props => {
      switch (props.status) {
        case 'draft':
          return `background: #9CA3AF;`;
        case 'scheduled':
          return `background: #3B82F6;`;
        case 'active':
          return `
            background: #22C55E;
            animation: pulse 1.5s ease-in-out infinite;
          `;
        case 'completed':
          return `background: #8B5CF6;`;
        default:
          return `background: #9CA3AF;`;
      }
    }}
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.3); }
  }
`;


const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  
  &.actions {
    opacity: 0;
  }
  
  @media (max-width: 1024px) {
    opacity: 1;
    transform: translateY(0);
    
    &.actions {
      opacity: 1;
    }
  }
`;

const ActionButton = styled.button<{ variant?: 'view' | 'edit' | 'delete' | 'cancel' | 'end' | 'progress' | 'results' | 'download' }>`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 5px;
  
  ${props => {
    if (props.variant === 'view') return `
      background: rgba(22, 119, 255, 0.1);
      color: #1677ff;
      
      &:hover {
        background: rgba(22, 119, 255, 0.2);
        transform: translateY(-1px);
      }
    `;
    if (props.variant === 'edit') return `
      background: rgba(139, 92, 246, 0.1);
      color: #8B5CF6;
      
      &:hover {
        background: rgba(139, 92, 246, 0.2);
        transform: translateY(-1px);
      }
    `;
    if (props.variant === 'delete') return `
      background: rgba(239, 68, 68, 0.1);
      color: #EF4444;
      
      &:hover {
        background: rgba(239, 68, 68, 0.2);
        transform: translateY(-1px);
      }
    `;
    if (props.variant === 'cancel') return `
      background: rgba(107, 114, 128, 0.1);
      color: #6B7280;
      
      &:hover {
        background: rgba(107, 114, 128, 0.2);
        transform: translateY(-1px);
      }
    `;
    if (props.variant === 'end') return `
      background: rgba(245, 158, 11, 0.1);
      color: #D97706;
      
      &:hover {
        background: rgba(245, 158, 11, 0.2);
        transform: translateY(-1px);
      }
    `;
    if (props.variant === 'progress') return `
      background: rgba(34, 197, 94, 0.1);
      color: #16A34A;
      
      &:hover {
        background: rgba(34, 197, 94, 0.2);
        transform: translateY(-1px);
      }
    `;
    if (props.variant === 'results') return `
      background: rgba(139, 92, 246, 0.1);
      color: #7C3AED;
      
      &:hover {
        background: rgba(139, 92, 246, 0.2);
        transform: translateY(-1px);
      }
    `;
    if (props.variant === 'download') return `
      background: rgba(59, 130, 246, 0.1);
      color: #2563EB;
      
      &:hover {
        background: rgba(59, 130, 246, 0.2);
        transform: translateY(-1px);
      }
    `;
    return '';
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(0, 0, 0, 0.45);
  
  .icon {
    font-size: 64px;
    margin-bottom: 16px;
    display: block;
  }
  
  .title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.65);
  }
  
  .description {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const LoadingState = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const SkeletonCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  
  .skeleton-line {
    height: 12px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 6px;
    margin-bottom: 12px;
    
    &.title {
      height: 20px;
      width: 70%;
    }
    
    &.subtitle {
      height: 16px;
      width: 90%;
    }
    
    &.meta {
      height: 12px;
      width: 50%;
    }
  }
  
  @keyframes loading {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const AssessmentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Redux state
  const { assessments, loading, create: createState, update: updateState, delete: deleteState } = useTypedSelector<IAssessmentsState>(state => state.assessments);
  const { classes } = useTypedSelector<IClassesState>(state => state.classes);
  const { answerSheets } = useTypedSelector<IAnswerSheetsState>(state => state.answer_sheets);
  const { gradingScales } = useTypedSelector<IGradingScalesState>(state => state.grading_scales);
  
  const { 
    getAssessmentsRequest, 
    createAssessmentRequest, 
    updateAssessmentRequest,
    deleteAssessmentRequest,
    getClassesRequest,
    getAnswerSheetsRequest,
    getGradingScalesRequest
  } = useActions();
  
  // Local state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<{ isOpen: boolean; assessment: any | null }>({
    isOpen: false,
    assessment: null
  });
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; assessmentId: number | null; assessmentName: string }>({ 
    isOpen: false, 
    assessmentId: null, 
    assessmentName: '' 
  });

  // Handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCreateAssessment = () => {
    setCreateModalVisible(true);
  };

  const handleCreateModalClose = () => {
    setCreateModalVisible(false);
  };

  const handleCreateSubmit = (data: AssessmentFormData) => {
    // Validate required fields
    if (!data.omrSheetId || !data.gradingScaleId || data.classIds.length === 0) {
      return;
    }
    
    // Map to new API model format
    const assessmentModel = {
      name: data.name,
      type: data.type,
      status: data.status,
      scheduledDate: data.date || null,
      duration: data.duration,
      description: data.description || null,
      classIds: data.classIds,
      omrSheetId: data.omrSheetId,
      gradingScaleId: data.gradingScaleId
    };
    createAssessmentRequest(assessmentModel);
  };

  const handleViewDetails = (assessmentId: number) => {
    // Find assessment and navigate to its details page
    const assessment: any = assessments?.find((a: any) => a.id === assessmentId);
    if (assessment) {
      const assessmentName = (assessment.examName || assessment.name || 'assessment')
        .toLowerCase()
        .replace(/\s+/g, '-');
      const encodedName = encodeURIComponent(assessmentName);
      navigate(`/assessment/${encodedName}?id=${assessmentId}`);
    }
  };

  const handleViewProgress = (assessmentId: number) => {
    notificationController.info({
      message: 'View Progress',
      description: `View progress for active assessment will be implemented soon.`
    });
  };

  const handleViewResults = (assessmentId: number) => {
    notificationController.info({
      message: 'View Results',
      description: `View results will be implemented soon.`
    });
  };

  const handleDownloadReports = (assessmentId: number) => {
    notificationController.info({
      message: 'Download Reports',
      description: `Download functionality will be implemented soon.`
    });
  };

  const handleCancelAssessment = (assessment: any) => {
    // Change status from Scheduled to Draft, clear date and duration
    const updateModel = {
      status: 'draft' as const,
      scheduledDate: '',
      duration: 0
    };
    updateAssessmentRequest(assessment.id, updateModel);
    notificationController.success({
      message: 'Assessment Cancelled',
      description: 'Assessment status changed to Draft.'
    });
  };

  const handleEndAssessment = (assessment: any) => {
    // Change status from Active to Completed
    const updateModel: { status: 'draft' | 'scheduled' | 'active' | 'completed' } = {
      status: 'completed'
    };
    updateAssessmentRequest(assessment.id, updateModel);
    notificationController.success({
      message: 'Assessment Ended',
      description: 'Assessment status changed to Completed.'
    });
  };

  const handleEditAssessment = (assessment: any) => {
    setEditModal({ isOpen: true, assessment });
  };

  const handleEditModalClose = () => {
    setEditModal({ isOpen: false, assessment: null });
  };

  const handleEditSubmit = (id: number, data: EditFormData) => {
    // Map to API model format
    const updateModel = {
      name: data.name,
      type: data.type,
      status: data.status,
      scheduledDate: data.date || '',
      duration: data.duration || 0,
      description: data.description || '',
      classIds: data.classIds,
      omrSheetId: data.omrSheetId || null,
      gradingScaleId: data.gradingScaleId || null
    };
    console.log('[EDIT] Sending update:', updateModel);
    updateAssessmentRequest(id, updateModel);
  };

  const handleDeleteClick = (assessmentId: number, assessmentName: string) => {
    setDeleteModal({ isOpen: true, assessmentId, assessmentName });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.assessmentId) {
      deleteAssessmentRequest(deleteModal.assessmentId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, assessmentId: null, assessmentName: '' });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter assessments based on search query
  const filteredAssessments = (assessments || []).filter((assessment: any) =>
    (assessment.examName || assessment.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get names for IDs
  const getClassName = (classId: number) => {
    const classesArray = classes as any[];
    const cls = classesArray?.find((c) => c.id === classId);
    return cls?.name || `Class ${classId}`;
  };

  const getAnswerSheetName = (answerSheetId: number) => {
    const sheetsArray = answerSheets as any[];
    const sheet = sheetsArray?.find((s) => s.id === answerSheetId);
    return sheet?.name || `Sheet ${answerSheetId}`;
  };

  const getGradingScaleName = (gradingScaleId: number) => {
    const scalesArray = gradingScales as any[];
    const scale = scalesArray?.find((s) => s.id === gradingScaleId);
    return scale?.name || `Scale ${gradingScaleId}`;
  };

  // Effects
  useEffect(() => {
    // Load all required data (userId already in localStorage from Keycloak)
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null') {
      // Add userId to URL if not present
      if (!searchParams.get('userId')) {
        setSearchParams({ userId: storedUserId });
      }
      
      getAssessmentsRequest();
      getClassesRequest();
      getAnswerSheetsRequest();
      getGradingScalesRequest();
    }
  }, []);

  // Handle successful deletion
  useEffect(() => {
    if (deleteState?.success) {
      setDeleteModal({ isOpen: false, assessmentId: null, assessmentName: '' });
      notificationController.success({
        message: 'Success',
        description: 'Assessment deleted successfully!'
      });
      getAssessmentsRequest();
    }
  }, [deleteState?.success]);

  // Handle delete error
  useEffect(() => {
    if (deleteState?.error) {
      notificationController.error({
        message: 'Failed to delete assessment',
        description: deleteState.error
      });
    }
  }, [deleteState?.error]);

  // Handle create success
  useEffect(() => {
    if (createState?.success) {
      setCreateModalVisible(false);
      notificationController.success({
        message: 'Success',
        description: 'Assessment created successfully!'
      });
      getAssessmentsRequest();
    }
  }, [createState?.success]);

  // Handle create error
  useEffect(() => {
    if (createState?.error) {
      notificationController.error({
        message: 'Failed to create assessment',
        description: createState.error
      });
    }
  }, [createState?.error]);

  // Handle update success
  useEffect(() => {
    if (updateState?.success) {
      setEditModal({ isOpen: false, assessment: null });
      notificationController.success({
        message: 'Success',
        description: 'Assessment updated successfully!'
      });
      getAssessmentsRequest();
    }
  }, [updateState?.success]);

  // Handle update error
  useEffect(() => {
    if (updateState?.error) {
      notificationController.error({
        message: 'Failed to update assessment',
        description: updateState.error
      });
    }
  }, [updateState?.error]);

  return (
    <>
      <PageTitle 
        title="Assessments" 
        description="Manage your class assessments" 
        url={window.location.href} 
        noIndex={true}
      />
      
      <PageContainer>
        {/* Header Section */}
        <HeaderSection>
          <TitleSection>
            <div>
              <h1 className="title">Assessments</h1>
              <p className="subtitle">Create and manage assessments for your classes</p>
            </div>
          </TitleSection>
          
          <ActionsSection>
            <SearchInput>
              <SearchOutlined className="search-icon" />
              <input
                type="text"
                placeholder="Search assessments..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </SearchInput>
            
            <CreateButton
              variant="primary"
              businessSize="medium"
              onClick={handleCreateAssessment}
              icon={<PlusOutlined />}
            >
              Create Assessment
            </CreateButton>
          </ActionsSection>
        </HeaderSection>

        {/* Content Section */}
        <ContentCard>
          {loading ? (
            <LoadingState>
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <SkeletonCard key={item}>
                  <div className="skeleton-line title"></div>
                  <div className="skeleton-line subtitle"></div>
                  <div className="skeleton-line meta"></div>
                </SkeletonCard>
              ))}
            </LoadingState>
          ) : filteredAssessments.length > 0 ? (
            <AssessmentsGrid>
              {filteredAssessments.map((assessment: any) => (
                <AssessmentCard key={assessment.id}>
                  {/* 📄 Assessment Name + 📅 Date */}
                  <CardTitle>
                    <span className="icon">📄</span>
                    <h3 className="name">{assessment.examName || assessment.name || 'Untitled Assessment'}</h3>
                    <span className="date">
                      📅 {assessment.scheduledDate && assessment.scheduledDate.length > 0
                        ? formatDate(assessment.scheduledDate) 
                        : 'Not scheduled'}
                    </span>
                  </CardTitle>
                  
                  <CardInfoList>
                    {/* 📝 Type */}
                    <CardInfoRow>
                      <span className="row-icon">📝</span>
                      <span className="row-label">Type</span>
                      <span className="row-value" style={{ textTransform: 'capitalize' }}>
                        {assessment.type || 'quiz'}
                      </span>
                    </CardInfoRow>
                    
                    {/* 🏫 Classes */}
                    <CardInfoRow>
                      <span className="row-icon">🏫</span>
                      <span className="row-label">Classes</span>
                      <span className="row-value">
                        {assessment.classIds?.length 
                          ? assessment.classIds.map((id: number) => getClassName(id)).join(', ')
                          : assessment.classId 
                            ? getClassName(assessment.classId)
                            : '—'}
                      </span>
                    </CardInfoRow>
                    
                    {/* 🔄 Status */}
                    <CardInfoRow>
                      <span className="row-icon">🔄</span>
                      <span className="row-label">Status</span>
                      <StatusText status={assessment.status || 'draft'}>
                        {(assessment.status || 'draft').charAt(0).toUpperCase() + (assessment.status || 'draft').slice(1)}
                      </StatusText>
                    </CardInfoRow>
                    
                    {/* ⏱ Duration */}
                    <CardInfoRow>
                      <span className="row-icon">⏱️</span>
                      <span className="row-label">Duration</span>
                      <span className="row-value">
                        {assessment.duration && assessment.duration > 0
                          ? `${assessment.duration} min`
                          : '—'}
                      </span>
                    </CardInfoRow>
                  </CardInfoList>
                  
                  <CardActions className="actions">
                    {/* Draft: Edit, Delete */}
                    {(assessment.status === 'draft' || !assessment.status) && (
                      <>
                        <ActionButton 
                          variant="edit" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAssessment(assessment);
                          }}
                        >
                          <EditOutlined />
                          Edit
                        </ActionButton>
                        <ActionButton 
                          variant="delete" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(assessment.id, assessment.examName || assessment.name);
                          }}
                        >
                          <DeleteOutlined />
                          Delete
                        </ActionButton>
                      </>
                    )}
                    
                    {/* Scheduled: Edit, Cancel, View details */}
                    {assessment.status === 'scheduled' && (
                      <>
                        <ActionButton 
                          variant="edit" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAssessment(assessment);
                          }}
                        >
                          <EditOutlined />
                          Edit
                        </ActionButton>
                        <ActionButton 
                          variant="cancel" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelAssessment(assessment);
                          }}
                        >
                          <CloseCircleOutlined />
                          Cancel
                        </ActionButton>
                        <ActionButton 
                          variant="view" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(assessment.id);
                          }}
                        >
                          <FileTextOutlined />
                          Details
                        </ActionButton>
                      </>
                    )}
                    
                    {/* Active: View progress, End assessment */}
                    {assessment.status === 'active' && (
                      <>
                        <ActionButton 
                          variant="progress" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewProgress(assessment.id);
                          }}
                        >
                          <PlayCircleOutlined />
                          Progress
                        </ActionButton>
                        <ActionButton 
                          variant="end" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEndAssessment(assessment);
                          }}
                        >
                          <StopOutlined />
                          End
                        </ActionButton>
                      </>
                    )}
                    
                    {/* Completed: View results, Download reports */}
                    {assessment.status === 'completed' && (
                      <>
                        <ActionButton 
                          variant="results" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewResults(assessment.id);
                          }}
                        >
                          <BarChartOutlined />
                          Results
                        </ActionButton>
                        <ActionButton 
                          variant="download" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadReports(assessment.id);
                          }}
                        >
                          <DownloadOutlined />
                          Download
                        </ActionButton>
                      </>
                    )}
                  </CardActions>
                </AssessmentCard>
              ))}
            </AssessmentsGrid>
          ) : (
            <EmptyState>
              <span className="icon">📋</span>
              <div className="title">
                {searchQuery ? 'No matching assessments found' : 'No assessments yet'}
              </div>
              <div className="description">
                {searchQuery 
                  ? `Try adjusting your search for "${searchQuery}"` 
                  : 'Create your first assessment to start grading your students'
                }
              </div>
              {!searchQuery && (
                <CreateButton
                  variant="primary"
                  businessSize="medium"
                  onClick={handleCreateAssessment}
                  icon={<PlusOutlined />}
                >
                  Create Your First Assessment
                </CreateButton>
              )}
            </EmptyState>
          )}
        </ContentCard>
        
        {/* Create Assessment Modal */}
        <CreateAssessmentModal
          isOpen={createModalVisible}
          onClose={handleCreateModalClose}
          onSubmit={handleCreateSubmit}
          isLoading={createState?.loading}
          classes={(classes || []).map((c: any) => ({ id: c.id, name: c.name }))}
          answerSheets={(answerSheets || []).map((s: any) => ({ id: s.id, name: s.name }))}
          gradingScales={(gradingScales || []).map((s: any) => ({ id: s.id, name: s.name }))}
        />

        {/* Edit Assessment Modal */}
        <EditAssessmentModal
          isOpen={editModal.isOpen}
          onClose={handleEditModalClose}
          onSubmit={handleEditSubmit}
          isLoading={updateState?.loading}
          assessment={editModal.assessment}
          classes={(classes || []).map((c: any) => ({ id: c.id, name: c.name }))}
          answerSheets={(answerSheets || []).map((s: any) => ({ id: s.id, name: s.name }))}
          gradingScales={(gradingScales || []).map((s: any) => ({ id: s.id, name: s.name }))}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={deleteModal.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Assessment"
          itemName={deleteModal.assessmentName}
          warningText="This action cannot be undone. The assessment and all related data will be permanently deleted."
          confirmButtonText="Delete"
          isLoading={deleteState?.loading}
        />
      </PageContainer>
    </>
  );
};

export default AssessmentsPage;
